import React from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ApiService } from '../../hooks';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useParams } from 'react-router-dom';
import { RefreshCw, Thermometer } from 'lucide-react';
import { Button } from '../../elements/ui/button';
import dayjs from 'dayjs';

import CombinedClimateCard from './components/CombinedClimateCard';
import WindCard from './components/WindCard';
import RadialGauge, { type GaugeThreshold } from './components/RadialGauge';
import SensorAreaChart from './components/SensorAreaChart';
import SensorLineChart from './components/SensorLineChart';
import DailyDeltaChart from './components/DailyDeltaChart';
import DailyBarChart from './components/DailyBarChart';
import HumidityLineChart from './components/HumidityLineChart';
import Co2DailySpreadChart from './components/Co2DailySpreadChart';
import DualAxisLineChart from './components/DualAxisLineChart';
import THITimeSeriesChart from './components/THITimeSeriesChart';
import type { TimeRange } from './components/timeSeries';
import {
  type ClimateReadings,
  type ClimateSeries,
  noReadings,
  noSeries,
  toClimateReadings,
  toClimateSeries
} from '../../models/location-climate';

const SERIES_DAYS = 7;

const co2Thresholds: GaugeThreshold[] = [
  { label: 'Good', max: 2500, color: '#22c55e', badgeVariant: 'success' },
  { label: 'Moderate', max: 3500, color: '#f59e0b', badgeVariant: 'warning' },
  { label: 'Danger', max: 5000, color: '#ef4444', badgeVariant: 'danger' }
];

const nh3Thresholds: GaugeThreshold[] = [
  { label: 'Good', max: 10, color: '#22c55e', badgeVariant: 'success' },
  { label: 'Moderate', max: 20, color: '#f59e0b', badgeVariant: 'warning' },
  { label: 'High', max: 35, color: '#f97316', badgeVariant: 'orange' },
  { label: 'Danger', max: 60, color: '#ef4444', badgeVariant: 'danger' }
];

const humidityThresholds: GaugeThreshold[] = [
  { label: 'Low', max: 40, color: '#ef4444', badgeVariant: 'danger' },
  { label: 'Dry', max: 50, color: '#f59e0b', badgeVariant: 'warning' },
  { label: 'Optimal', max: 70, color: '#22c55e', badgeVariant: 'success' },
  { label: 'Humid', max: 80, color: '#f59e0b', badgeVariant: 'warning' },
  { label: 'High', max: 100, color: '#ef4444', badgeVariant: 'danger' }
];

const heatStressThresholds: GaugeThreshold[] = [
  { label: 'Normal', max: 72, color: '#22c55e', badgeVariant: 'success' },
  { label: 'Alert', max: 88, color: '#f59e0b', badgeVariant: 'warning' },
  { label: 'Danger', max: 100, color: '#ef4444', badgeVariant: 'danger' }
];

const LocationClimateViewV2: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const apiService = React.useMemo(() => new ApiService(), []);

  const [isBusy, setIsBusy] = React.useState(true);
  const [periodDate, setPeriodDate] = React.useState<Date | null>(new Date());
  const [reloadToken, setReloadToken] = React.useState(0);

  const [series, setSeries] = React.useState<ClimateSeries>(noSeries);
  const [readings, setReadings] = React.useState<ClimateReadings>(noReadings);

  // The window the series are asked for is also the axis domain of every chart. Taking it from the
  // request rather than from the response is what lets a chart show that a sensor was silent for a
  // day, instead of quietly drawing a shorter axis out of the hours that did arrive.
  const range: TimeRange = React.useMemo(() => {
    const to = dayjs(periodDate ?? new Date());

    return { from: to.subtract(SERIES_DAYS, 'day').valueOf(), to: to.valueOf() };
  }, [periodDate]);

  React.useEffect(() => {
    if (!periodDate || !id) {
      return;
    }

    let cancelled = false;

    setIsBusy(true);

    apiService.api.locations
      .locationsViewClimateLocation({
        ClimateDate: dayjs(range.to).toISOString(),
        Id: id,
        SeriesFrom: dayjs(range.from).toISOString(),
        SeriesTo: dayjs(range.to).toISOString()
      })
      .then((result) => {
        if (cancelled) {
          return;
        }

        const data = result.data;

        setReadings(toClimateReadings(data));
        setSeries(toClimateSeries(data?.timeSeriesData));
      })
      .catch((e) => {
        if (cancelled) {
          return;
        }

        setSeries(noSeries);
        setReadings(noReadings);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (!cancelled) {
          setIsBusy(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiService, id, periodDate, range, reloadToken, t]);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Thermometer className="h-6 w-6 text-gray-700" />
          <h1 className="text-xl font-semibold text-gray-900">{t('f-loc-climate-view-title')}</h1>
        </div>
        <div className="flex items-center gap-3">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select date"
              value={periodDate}
              onChange={(val) => setPeriodDate(dayjs(val).toDate())}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'outlined',
                  sx: { width: 180 }
                }
              }}
            />
          </LocalizationProvider>
          <Button variant="outline" size="icon" onClick={() => setReloadToken((token) => token + 1)} disabled={isBusy}>
            <RefreshCw className={`h-4 w-4 ${isBusy ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isBusy && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin" />
        </div>
      )}

      {/* Content */}
      {!isBusy && (
        <div className="space-y-4">
          {/* Row 1: Climate Overview + Wind */}
          <div className="grid grid-cols-2 gap-4">
            <CombinedClimateCard
              temperatureInside={readings.temperatureInside}
              temperatureOutside={readings.temperatureOutside}
              humidityInside={readings.humidityInside}
              humidityOutside={readings.humidityOutside}
            />
            <WindCard
              windSpeed={readings.windSpeed}
              windDirection={readings.windDirection}
              windDegree={readings.windDegree}
            />
          </div>

          {/* Row 2: Gauges */}
          <div className="grid grid-cols-4 gap-4">
            <RadialGauge
              title={t('f-co2-sensor')}
              value={readings.co2}
              max={5000}
              unit="ppm"
              thresholds={co2Thresholds}
            />
            <RadialGauge
              title={t('f-nh3-sensor')}
              value={readings.nh3}
              max={60}
              unit="ppm"
              thresholds={nh3Thresholds}
            />
            <RadialGauge
              title={t('f-humidity-sensor')}
              value={readings.humidityInside}
              max={100}
              unit="%"
              thresholds={humidityThresholds}
            />
            <RadialGauge
              title={t('f-heat-stress-index-sensor')}
              value={readings.heatStressIndex}
              max={100}
              unit="THI"
              thresholds={heatStressThresholds}
            />
          </div>

          {/* Row 2b: Humidity */}
          <HumidityLineChart
            title={t('f-humidity-relative')}
            series={series.humidity}
            range={range}
            lowThreshold={50}
            highThreshold={80}
          />

          {/* Row 3: Temperature charts */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <SensorAreaChart
                title={t('f-temperature')}
                series={series.temperature}
                range={range}
                unit={readings.temperatureUnit}
              />
            </div>
            <DailyDeltaChart
              title={t('f-temperature-delta')}
              series={series.temperature}
              range={range}
              unit={readings.temperatureUnit}
            />
          </div>

          {/* Row 4: CO2 charts */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <SensorLineChart
                title={t('f-co2')}
                series={series.co2}
                range={range}
                unit="ppm"
                minThreshold={1000}
                maxThreshold={3000}
              />
            </div>
            <Co2DailySpreadChart title={t('f-co2-daily-spread')} series={series.co2} range={range} unit="ppm" />
          </div>

          {/* Row 5: NH3 + Outside Temperature (dual axis) */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <DualAxisLineChart
                title={t('f-nh3')}
                series1={series.nh3}
                unit1="ppm"
                series2={series.outsideTemperature}
                unit2={readings.temperatureUnit}
                range={range}
              />
            </div>
            <DailyBarChart title={t('f-nh3')} series={series.nh3} range={range} unit="ppm" />
          </div>

          {/* Row 6: THI Heat Stress Index */}
          <THITimeSeriesChart title={t('f-thi-index')} series={series.thi} range={range} />
        </div>
      )}
    </div>
  );
};

export default LocationClimateViewV2;
